/**
 * Admin Users Component
 * User management: list, ban/unban, change roles
 */

import { useEffect, useState, useCallback } from 'react';
import { LoadingSpinner, ConfirmDialog } from '../ui';

interface UserData {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: 'USER' | 'ADMIN';
  isBanned: boolean;
  bannedReason: string | null;
  createdAt: string;
  stats: {
    totalCreaturesSpawned: number;
    longestSurvival: number;
    totalPlayTime: number;
  };
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export function AdminUsers() {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [banModal, setBanModal] = useState<{ userId: string; userName: string } | null>(null);
  const [banReason, setBanReason] = useState('');
  const [roleConfirm, setRoleConfirm] = useState<{ userId: string; userName: string; currentRole: string } | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data.users);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleBan = async () => {
    if (!banModal) return;

    try {
      const response = await fetch(`${API_URL}/admin/users/${banModal.userId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: banReason }),
      });

      if (!response.ok) throw new Error('Failed to ban user');

      setBanModal(null);
      setBanReason('');
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error banning user');
    }
  };

  const handleUnban = async (userId: string) => {
    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/unban`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to unban user');
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error unbanning user');
    }
  };

  const handleToggleRole = async () => {
    if (!roleConfirm) return;
    const newRole = roleConfirm.currentRole === 'ADMIN' ? 'USER' : 'ADMIN';

    try {
      const response = await fetch(`${API_URL}/admin/users/${roleConfirm.userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) throw new Error('Failed to update role');
      setRoleConfirm(null);
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating role');
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = search.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.name && user.name.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <LoadingSpinner text="Cargando usuarios..." />
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <h2>Gestión de Usuarios ({users.length})</h2>
        <input
          type="text"
          className="admin-search"
          placeholder="Buscar por nombre o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="admin-error">{error}</div>}

      <table className="admin-users-table">
        <thead>
          <tr>
            <th>Usuario</th>
            <th>Rol</th>
            <th>Estado</th>
            <th>Criaturas</th>
            <th>Tiempo de juego</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user) => (
            <tr key={user.id}>
              <td>
                <div className="admin-user-cell">
                  {user.image && (
                    <img src={user.image} alt="" className="admin-user-avatar" />
                  )}
                  <div className="admin-user-info">
                    <span className="admin-user-name">{user.name || 'Sin nombre'}</span>
                    <span className="admin-user-email">{user.email}</span>
                  </div>
                </div>
              </td>
              <td>
                <span className={`admin-badge ${user.role.toLowerCase()}`}>
                  {user.role}
                </span>
              </td>
              <td>
                {user.isBanned ? (
                  <span className="admin-badge banned" title={user.bannedReason || ''}>
                    Baneado
                  </span>
                ) : (
                  <span className="admin-badge active">Activo</span>
                )}
              </td>
              <td>{user.stats?.totalCreaturesSpawned ?? 0}</td>
              <td>{Math.round((user.stats?.totalPlayTime ?? 0) / 60)}m</td>
              <td>
                <div className="admin-actions">
                  <button
                    className="admin-action-btn"
                    onClick={() => setRoleConfirm({ userId: user.id, userName: user.name || user.email, currentRole: user.role })}
                    title={user.role === 'ADMIN' ? 'Quitar admin' : 'Hacer admin'}
                  >
                    {user.role === 'ADMIN' ? 'Degradar' : 'Promover'}
                  </button>
                  {user.isBanned ? (
                    <button
                      className="admin-action-btn success"
                      onClick={() => handleUnban(user.id)}
                    >
                      Desbanear
                    </button>
                  ) : (
                    <button
                      className="admin-action-btn danger"
                      onClick={() => setBanModal({ userId: user.id, userName: user.name || user.email })}
                    >
                      Banear
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Ban Modal */}
      {banModal && (
        <div className="admin-modal-overlay" onClick={() => setBanModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Banear usuario: {banModal.userName}</h3>
            <p style={{ color: '#a0a0c0', marginBottom: '1rem' }}>
              El usuario no podrá acceder a la aplicación.
            </p>
            <textarea
              className="admin-textarea"
              placeholder="Razón del ban (opcional)..."
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
            />
            <div className="admin-modal-actions">
              <button
                className="admin-action-btn"
                onClick={() => setBanModal(null)}
              >
                Cancelar
              </button>
              <button
                className="admin-sim-btn danger"
                onClick={handleBan}
              >
                Confirmar Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Confirmation */}
      {roleConfirm && (
        <ConfirmDialog
          title={roleConfirm.currentRole === 'ADMIN' ? 'Degradar usuario' : 'Promover usuario'}
          message={
            roleConfirm.currentRole === 'ADMIN'
              ? `¿Quitar permisos de administrador a ${roleConfirm.userName}?`
              : `¿Dar permisos de administrador a ${roleConfirm.userName}?`
          }
          confirmText={roleConfirm.currentRole === 'ADMIN' ? 'Degradar' : 'Promover'}
          variant={roleConfirm.currentRole === 'ADMIN' ? 'danger' : 'default'}
          onConfirm={handleToggleRole}
          onCancel={() => setRoleConfirm(null)}
        />
      )}
    </div>
  );
}
